import React, { useEffect, useRef, useState } from "react";
import { Dropdown, Menu, Spin, Table, TableColumnsType } from "antd";
import InfiniteScroll from "react-infinite-scroll-component";
import { useTranslation } from "react-i18next";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../helpers/permissionModule";
import {
  formatToLocalDate,
  getAntdSortOrder,
  useDebounce,
} from "../../../helpers/utils";
import { getDecryptedName } from "../../../helpers/bycrypt";
import { svg } from "../../../Assets/svg";
import FullScreenLoader from "../../Components/FullScreenLoader";
import NoDataMessage from "../../Components/NoData";
import NoPermissionPage from "../../Components/NoPermissionPage";
import NotificationButton from "../../Components/NotificationButton";
import ModifyStorageTypeModal from "./Components/Modals/ModifyStorageTypeModal";
import { getStorageTypesAPI } from "./storageTypesApis";

const StorageTypes: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData?.MASTER_DATA,
  );
  const { authLoading } = useAppSelector((state: RootState) => state?.loading);

  const abortControllerRef = useRef<AbortController | null>(null);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit] = useState<number>(15);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);

  const [storageTypes, setStorageTypes] = useState<any[]>([]);
  const [selectedStorageType, setSelectedStorageType] = useState<any>(null);
  const [isModifyModalOpen, setIsModifyModalOpen] = useState<boolean>(false);

  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 300);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 991.98);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 991.98);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [sorterState, setSorterState] = useState<{
    field: string | null;
    order: "asc" | "desc" | null;
  }>({ field: null, order: null });

  const fetchStorageTypes = (page: number, isReset: boolean) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (isReset) {
      setLoading(true);
    }

    const filters = {
      page: page,
      limit: limit,
      search: debouncedSearch,
      sortBy: sorterState.field,
      sortOrder: sorterState.order,
    };

    dispatch(
      getStorageTypesAPI({
        filters,
        signal: controller.signal,
        onSuccess: (data: any) => {
          const newData = data?.data || (Array.isArray(data) ? data : []);
          const total = data?.total ?? data?.totalCount ?? newData.length;
          setTotalCount(total);

          if (isReset) {
            setStorageTypes(newData);
          } else {
            setStorageTypes((prev) => [...prev, ...newData]);
          }

          const currentCount = isReset
            ? newData.length
            : storageTypes.length + newData.length;
          setHasMore(currentCount < total);
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
        },
      }),
    );
  };

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchStorageTypes(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, sorterState]);

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchStorageTypes(nextPage, false);
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const order =
      singleSorter?.order === "ascend"
        ? "asc"
        : singleSorter?.order === "descend"
        ? "desc"
        : null;
    const field = singleSorter?.field || singleSorter?.columnKey || null;
    setSorterState({ field, order: order as any });
  };

  const handleModifyClick = (record: any) => {
    setSelectedStorageType(record);
    setIsModifyModalOpen(true);
  };

  const MoreOptionsMenu = (data: any) => {
    return (
      <Menu>
        <Menu.Item key="1" onClick={() => handleModifyClick(data)}>
          <div className="text-black d-flex align-items-center gap-2 svg-20">
            Modify
          </div>
        </Menu.Item>
      </Menu>
    );
  };

  const columns: TableColumnsType<any> = [
    {
      title: "Storage Type",
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortOrder: getAntdSortOrder("name", sorterState),
      render: (text: string, record: any) => {
        const title = text || record?.name || record?.storage_type || "-";
        return <div className="fw-bold">{title}</div>;
      },
    },
    {
      title: "Sage Group",
      dataIndex: "sage_product_group_code",
      key: "sage_product_group_code",
      sorter: true,
      sortOrder: getAntdSortOrder("sage_product_group_code", sorterState),
      render: (code: string, record: any) => {
        const groupCode = code || record?.sage_product_group_code || "-";
        return <div>{groupCode}</div>;
      },
    },
    {
      title: "Updated On",
      dataIndex: "updated_at",
      key: "updated_at",
      sorter: true,
      sortOrder: getAntdSortOrder("updated_at", sorterState),
      render: (date: string, record: any) => {
        const dateValue = date || record?.updated_at || record?.created_at;
        return <div>{dateValue ? formatToLocalDate(dateValue, true) : "-"}</div>;
      },
    },
    {
      title: "Updated By",
      dataIndex: "updated_by",
      key: "updated_by",
      sorter: true,
      sortOrder: getAntdSortOrder("updated_by", sorterState),
      render: (_: any, record: any) => {
        const name =
          getDecryptedName(record?.updated_by) ||
          (typeof record?.updated_by === "string" ? record.updated_by : "") ||
          "-";
        return <div>{name}</div>;
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center" as const,
      width: "5%",
      render: (_: unknown, record: any) => (
        <div
          className="d-flex justify-content-center"
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            overlay={() => MoreOptionsMenu(record)}
            trigger={["click"]}
            placement="bottomRight"
            className="cursor-pointer"
            overlayClassName="moreOptionDropdown"
          >
            <span className="svg-28 svg-outline-primary" role="button">
              {svg?.tableMoreOption}
            </span>
          </Dropdown>
        </div>
      ),
    },
  ];

  return USER_PERMISSIONS?.READ ? (
    <div className="page-content d-flex flex-column h-100 px-40 py-40">
      <div className="d-flex flex-md-nowrap flex-wrap gap-md-3 gap-2 justify-content-between align-items-sm-center align-items-start mb-md-4 mb-3">
        <h2 className="text-black fs-36 fw-bolder page-title mb-0">
          Storage Types {totalCount > 0 && `(${totalCount})`}
        </h2>
        <div className="pageSearch mw-100 position-relative d-inline-block order-sm-2 order-3 ms-auto">
          <input
            type="text"
            value={search}
            className="form-control form-control-lg"
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
          />
          {search?.length ? (
            <span className="cursor-pointer" onClick={() => setSearch("")}>
              {svg?.searchClear}
            </span>
          ) : (
            <span>{svg?.search}</span>
          )}
        </div>
        <div className="d-flex gap-sm-3 gap-2 order-sm-3 order-2">
          <NotificationButton />
        </div>
      </div>

      <div className="card page-table-card page-table-device border-0 shadow rounded-4 flex-fill overflow-y-auto" style={{ minHeight: 0 }}>
        <div className="card-body overflow-y-auto p-0" id="scrollableDiv">
          {loading && storageTypes?.length === 0 ? (
            <FullScreenLoader />
          ) : storageTypes?.length === 0 ? (
            <NoDataMessage
              icon={svg?.noMasterDataIcon || svg?.noPricesheetInfoDataL}
              text="You do not have any Storage Types yet."
            />
          ) : (
            <InfiniteScroll
              dataLength={storageTypes.length}
              next={handleLoadMore}
              hasMore={hasMore}
              loader={
                <div className="text-center p-2">
                  <Spin />
                </div>
              }
              scrollableTarget="scrollableDiv"
              height={"100%"}
            >
              {!isMobile ? (
                <Table
                  dataSource={storageTypes}
                  scroll={{ x: "max-content" }}
                  pagination={false}
                  columns={columns}
                  onChange={handleTableChange}
                  rowKey={(record, idx) =>
                    record?._id || record?.id || record?.slug || idx
                  }
                  className="custom-table"
                  bordered
                />
              ) : (
                <div className="px-3">
                  <div className="row gx-3 gy-3">
                    {storageTypes?.map((record: any, index: number) => {
                      const title = record?.name || record?.storage_type || "-";
                      const groupCode = record?.sage_product_group_code || "-";
                      const dateValue = record?.updated_at || record?.created_at;
                      const updatedOn = dateValue ? formatToLocalDate(dateValue, true) : "-";
                      const updatedBy =
                        getDecryptedName(record?.updated_by) ||
                        (typeof record?.updated_by === "string" ? record.updated_by : "") ||
                        "-";

                      return (
                        <div className="col-md-6" key={record?._id || record?.id || index}>
                          <div className="card mobile-table-card mobile-font-s rounded-4 p-3 h-100">
                            {/* Header: Storage Type Title & Action Dropdown */}
                            <div className="row align-items-start mb-2">
                              <div className="col-sm-5 col-5 text-secondary fw-medium">
                                Storage Type:
                              </div>
                              <div className="col-sm-7 col-7 d-flex justify-content-between gap-2">
                                <span
                                  className="fw-bold fs-5 text-break text-primary"
                                  style={{
                                    minWidth: 0,
                                    wordBreak: "break-word",
                                    overflowWrap: "anywhere",
                                    whiteSpace: "normal",
                                  }}
                                >
                                  {title}
                                </span>

                                <div className="flex-shrink-0 ms-2" onClick={(e) => e.stopPropagation()}>
                                  <Dropdown
                                    overlay={() => MoreOptionsMenu(record)}
                                    trigger={["click"]}
                                    placement="bottomRight"
                                    overlayClassName="moreOptionDropdown"
                                  >
                                    <span className="cursor-pointer d-inline-block">
                                      {svg?.tableMobileOption}
                                    </span>
                                  </Dropdown>
                                </div>
                              </div>
                            </div>

                            {/* Details Grid */}
                            <div className="mobile-card-details fs-6 fs-6-mob">
                              <div className="row gy-2">
                                <div className="col-12">
                                  <div className="row">
                                    <div className="col-sm-5 col-5 text-secondary fw-medium">
                                      Sage Group:
                                    </div>
                                    <div className="col-sm-7 col-7">
                                      {groupCode}
                                    </div>
                                  </div>
                                </div>

                                <div className="col-12">
                                  <div className="row">
                                    <div className="col-sm-5 col-5 text-secondary fw-medium">
                                      Updated On:
                                    </div>
                                    <div className="col-sm-7 col-7">
                                      {updatedOn}
                                    </div>
                                  </div>
                                </div>

                                <div className="col-12">
                                  <div className="row">
                                    <div className="col-sm-5 col-5 text-secondary fw-medium">
                                      Updated By:
                                    </div>
                                    <div className="col-sm-7 col-7">
                                      {updatedBy}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </InfiniteScroll>
          )}
        </div>
      </div>

      {isModifyModalOpen && (
        <ModifyStorageTypeModal
          open={isModifyModalOpen}
          onClose={() => {
            setSelectedStorageType(null);
            setIsModifyModalOpen(false);
          }}
          data={selectedStorageType}
          onSuccess={() => {
            fetchStorageTypes(1, true);
          }}
        />
      )}
    </div>
  ) : authLoading ? (
    <FullScreenLoader />
  ) : (
    <NoPermissionPage />
  );
};

export default StorageTypes;
